import * as React from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

function Input({ className, type, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none',
        'placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground',
        'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        // Native date pickers: keep the glyph clickable and legible on both themes.
        '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-80',
        // Number inputs: no spinner buttons — they are too small to hit and
        // the arrow keys already step the value.
        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        className,
      )}
      {...props}
    />
  );
}

/**
 * An input with an icon (or short text, like a currency symbol) inside its
 * leading edge, and optionally something inside its trailing edge.
 */
const IconInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & {
    icon?: React.ReactNode;
    trailing?: React.ReactNode;
    wrapperClassName?: string;
  }
>(function IconInput({ icon, trailing, className, wrapperClassName, ...props }, ref) {
  return (
    <div className={cn('relative w-full', wrapperClassName)}>
      {icon ? (
        <span className="pointer-events-none absolute start-3 top-1/2 flex -translate-y-1/2 items-center text-sm text-muted-foreground [&_svg]:size-4">
          {icon}
        </span>
      ) : null}
      <Input ref={ref} className={cn(icon && 'ps-9', trailing && 'pe-9', className)} {...props} />
      {trailing ? (
        <span className="absolute end-2 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground">
          {trailing}
        </span>
      ) : null}
    </div>
  );
});

/** Password input with a show/hide toggle on the trailing edge. */
const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<'input'>, 'type'> & { icon?: React.ReactNode }
>(function PasswordInput({ icon, ...props }, ref) {
  const [visible, setVisible] = React.useState(false);

  return (
    <IconInput
      ref={ref}
      type={visible ? 'text' : 'password'}
      icon={icon}
      trailing={
        <button
          type="button"
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="rounded-sm p-1 transition-colors hover:text-foreground [&_svg]:size-4"
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      }
      {...props}
    />
  );
});

export { Input, IconInput, PasswordInput };
